import isEqual from 'lodash.isequal';

import { Option } from 'effect';

import { Filter } from '@shared/modules/filter';
import { CSSProperties, ReactNode } from 'react';
import { To } from 'react-router-dom';

export class Range<T, F extends Filter = {}, S extends Filter.Sort = null> {
  constructor(
    readonly items: ReadonlyMap<number, T>,
    readonly total: number,
    readonly filter: F,
    readonly sort: S | null,
    readonly cursorSize: number,
  ) {}

  merge(newRange: Range<T, F, S>): Range<T, F, S> {
    if (isEqual(this.filter, newRange.filter)) {
      return new Range<T, F, S>(
        new Map<number, T>([...Array.from(this.items.entries()), ...Array.from(newRange.items.entries())]),
        newRange.total,
        newRange.filter,
        newRange.sort,
        newRange.cursorSize,
      );
    }

    return newRange;
  }

  has(index: number): boolean {
    return this.items.has(index);
  }

  get(index: number): Option.Option<T> {
    return Option.fromNullable(this.items.get(index));
  }

  toList(): Array<T> {
    return Array.from(this.items.values());
  }

  map<B>(fa: (a: T) => B): Range<B, F, S> {
    return new Range(
      new Map<number, B>(Array.from(this.items, ([key, value]) => [key, fa(value)])),
      this.total,
      this.filter,
      this.sort,
      this.cursorSize,
    );
  }

  static fromRangeResult<T, F extends Filter = {}, S extends Filter.Sort = null>(result: Range.Result<T, F, S>) {
    return new Range(
      new Map<number, T>(result.items.map((item, i) => [i + result.startIndex, item])),
      result.total,
      result.filter,
      result.sort,
      result.cursorSize,
    );
  }

  static fromArray<T, F extends Filter = {}, S extends Filter.Sort = null>(
    list: Array<T>,
    filter: F,
    sort: S | null = null,
  ): Range<T, F, S> {
    return new Range<T, F, S>(
      new Map<number, T>(list.map((item, i) => [i, item])),
      list.length,
      filter,
      sort,
      list.length,
    );
  }
}

export namespace Range {
  export class Cursor {
    static DEFAULT_SIZE = 50;

    constructor(
      public startIndex: number,
      public endIndex: number,
      public size: number = Cursor.DEFAULT_SIZE,
    ) {}

    static fromPage(page: number, size: number = Cursor.DEFAULT_SIZE) {
      return new Cursor((page - 1) * size, page * size - 1);
    }

    static initial(size: number = Cursor.DEFAULT_SIZE) {
      return Cursor.fromPage(1, size);
    }

    static fromIndex(index: number, size: number = Cursor.DEFAULT_SIZE) {
      const startIndex = Math.max(0, index - size / 2);

      return new Cursor(startIndex, startIndex + size - 1, size);
    }

    toPage() {
      return Math.floor(this.startIndex / this.size) + 1;
    }

    get queries() {
      return {
        startIndex: this.startIndex,
        endIndex: this.endIndex,
      };
    }
  }

  export interface Result<T, F extends Filter = {}, S extends Filter.Sort = null>
    extends Required<Omit<Cursor, 'size'>> {
    total: number;
    items: Array<T>;
    filter: F;
    sort: S | null;
    cursorSize: number;
  }

  export type NestedResult<Key extends string, T, F extends Filter = {}, S extends Filter.Sort = null> = {
    [key in Key]: Result<T, F, S>;
  };
}

export type VirtualizedRenderer<T> = (item: T, index: number) => ReactNode;

export interface VirtualizedListColumn<T> {
  key: Extract<keyof T, string> | string;
  label?: ReactNode;
  width?: string;
  show?: boolean;
  headerStyle?: CSSProperties;
  style?: CSSProperties;
  renderer?: VirtualizedRenderer<T>;
}

export type VirtualizedListDesktopTemplate<T> = Array<VirtualizedListColumn<T>>;
export type VirtualizedListRowStyle<T> = (item: T, index: number) => CSSProperties | undefined;
export type VirtualizedListRowLinkBuilder<T> = (item: T, index: number) => To | null | undefined;

export interface VirtualizedListTemplate<T> {
  desktopTemplate: VirtualizedListDesktopTemplate<T>;
  mobileTemplate?: VirtualizedRenderer<T>;
  rowStyle?: VirtualizedListRowStyle<T>;
  rowLinkBuilder?: VirtualizedListRowLinkBuilder<T>;
}

export interface VirtualizedListProps<T> extends VirtualizedListTemplate<T> {
  range: Range<T>;
  loadPage: (page: number) => void;
  getItemKey?: (item: T, index: number) => string | number;
}
